import { Chess } from 'chess.js';
import { START_FEN } from '@chessmate/protocol';
import type { UciEngine } from './uci-engine.js';

export interface FakeEngineOpts {
  /** UCI moves to play in order; falls back to the first legal move when exhausted. */
  readonly script?: readonly string[];
}

/**
 * Deterministic engine used by unit tests and e2e (`?engine=fake`). Speaks enough
 * UCI for ChessAI: uci/isready/setoption/position/go. Emits 3 MultiPV lines so the
 * coach can always return ≥2 suggestions.
 */
export function createFakeEngine(opts: FakeEngineOpts = {}): UciEngine {
  let fen = START_FEN;
  const script = [...(opts.script ?? [])];
  let onMsg: ((l: string) => void) | null = null;
  const emit = (l: string) => onMsg?.(l);

  return {
    postMessage(line: string): void {
      const cmd = line.trim();
      if (cmd === 'uci') {
        emit('id name S4K Fake 1.0');
        emit('uciok');
        return;
      }
      if (cmd === 'isready') {
        emit('readyok');
        return;
      }
      if (cmd.startsWith('position fen')) {
        const m = cmd.match(/^position fen (.+)$/);
        const next = m?.[1];
        if (next) fen = next;
        return;
      }
      if (cmd.startsWith('setoption')) return;
      if (cmd.startsWith('go')) {
        const chess = new Chess(fen);
        const legal = chess.moves({ verbose: true }).map((mv) => `${mv.from}${mv.to}`);
        // Look-ahead calls (`predictOpponentReply` in game-store) and robot
        // moves (`robotMove`) both examine the SAME position — the one
        // reached after the human's move, with the robot's side to move.
        // The script drives the robot's *actual* reply; the look-ahead is a
        // hint shown while the player is still selecting. The script head
        // IS legal for the side-to-move at that position, so a naive
        // `script.shift()` consumed the head on the look-ahead call and the
        // robot then fell back to `legal[0]` — chess.js's verbose-move
        // ordering put b8c6 ahead of e7e5, so `golden-path.spec.ts`'s
        // `expect('1. e4 e5')` failed with `'1. e4 Nc6'`.
        //
        // Fix: peek-only mode. The fake engine inspects the head without
        // shifting; the real `robotMove` consumes it later. Stockfish has
        // no script, so this branch is a no-op for the production engine —
        // it's purely a test/CI determinism lever.
        const peek = cmd.includes(' peek');
        const head = script[0];
        let chosen: string;
        if (head && legal.includes(head)) {
          chosen = peek ? head : (script.shift() ?? legal[0] ?? '');
        } else {
          chosen = legal[0] ?? '';
        }
        const alt = legal.filter((m) => m !== chosen).slice(0, 2);
        const pv = [chosen, ...alt];
        const withReply = (first: string) => {
          const probe = new Chess(fen);
          if (first) probe.move(first);
          const reply = probe.moves({ verbose: true })[0];
          return reply ? `${first} ${reply.from}${reply.to}` : first;
        };
        pv.forEach((m, i) => {
          emit(`info depth 1 seldepth 1 multipv ${i + 1} score cp 0 nodes 1 nps 1 time 1 pv ${withReply(m)}`);
        });
        emit(`bestmove ${chosen}`);
        return;
      }
      if (cmd === 'quit') return;
    },
    onMessage(cb: (l: string) => void): void {
      onMsg = cb;
    },
    terminate(): void {},
  };
}
